import {makeAutoObservable, action, runInAction} from "mobx";
import { getDatasetList, newDataset, delDataset, uploadImgToServer, delDatasetImgs, addDatasetImg, fetchDatasetImgs, updateDatasetImg, fetchPrompt } from "../utils/serverRequest";
import { delay, base64ToFile, getImgInfo, clipImgDummy, clipImgCenter, clipImgFocus } from "../utils/misc";
import { MSG_TYPE, msgbox } from "../utils/Msgbox";
import JSZip from "jszip";

const MAX_DATASET_IMT_COUNT: number = 100;

export class DatasetImgInfo{
    url: string = "";
    width: number = 0;
    height: number = 0;
    text: string = "";
    id: string = "";
    isTextChange: boolean = false; // 提示词是否有改动

    constructor(url: string, width: number, height: number, text: string, id: string){
        makeAutoObservable(this);
        this.url = url;
        this.width = width;
        this.height = height;
        this.text = text;
        this.id = id;
        this.isTextChange = false;
    }

    changeText(a: string){
        this.text = a;
        this.isTextChange = true;
    }

    static createFromJson(a: any): Promise<DatasetImgInfo>{
        const url = a.file;
        return new Promise<DatasetImgInfo>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onerror = (e) => {
                console.log(e);
                reject(e);
            };
            img.onload = () => {
                resolve(new DatasetImgInfo(url, img.width, img.height, a.captions || "", a.id));
            };
            img.src = url;
        });
    }

    static createArrayFromJson(a: any, index: number = 0, res: DatasetImgInfo[] = []): Promise<DatasetImgInfo[]>{
        if(index >= a.length){
            return Promise.resolve(res);
        }

        return this.createFromJson(a[index]).then(imgInfo => {
            res.push(imgInfo);
            return this.createArrayFromJson(a, index + 1, res);
        });
    }
}

export class DatasetInfo{
    title: string = "";
    description: string = "";
    videoCount: number = 0;
    imgs: DatasetImgInfo[] = [];
    totalImgCount: number = 0;
    id: string = "";

    constructor(title: string, description: string, videoCount: number, imgs: DatasetImgInfo[], id: string){
        makeAutoObservable(this);
        this.title = title;
        this.description = description;
        this.videoCount = videoCount;
        this.imgs = imgs;
        this.id = id;
        this.totalImgCount = 0;
    }

    static createFromJson(a: any){
        return new DatasetInfo(a.name, a.description, 0, [], a.id);
    }

    private downloadInner = (index: number, zip: JSZip): Promise<void> => {
        if(index >= this.imgs.length){
            return zip.generateAsync({type: "blob"}).then(res => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(res);
                a.download = `${Date.now()}.zip`;
                a.click();
                return Promise.resolve();
            })
        }

        const item = this.imgs[index];
        const url = item.url;
        const ext = url.substring(url.lastIndexOf("."));
        return fetch(url)
            .then(res => res.blob())
            .then(b => {
                zip.file(`${index}${ext}`, b);
                zip.file(`${index}.txt`, item.text);
                return this.downloadInner(index + 1, zip);
            });
    };

    download(): Promise<void>{
        if(this.imgs.length <= 0){
            return Promise.resolve();
        }
        return this.downloadInner(0, new JSZip());
    }
}

export class DatasetStore{
    private _datasets: DatasetInfo[] = [];
    private _curDataset: DatasetInfo | null = null;
    private _stopTrain: boolean = false;
    private _isCliping: boolean = false;

    constructor(){
        makeAutoObservable(this);
        this.hintOnRefresh();
    }

    get datasets(): DatasetInfo[]{
        return this._datasets;
    }

    get curDatasetInfo(): DatasetInfo|null{
        return this._curDataset;
    }

    private hintOnRefresh = (): void => {
        window.addEventListener("beforeunload", (event) => {
            if (this._isCliping) {
                event.preventDefault();
            }

            return "";
        });
    };

    @action
    init(): Promise<void>{
        let datasets: DatasetInfo[] = [];
        return getDatasetList().then(res => {
            datasets = res;
            return Promise.all(res.map(item => fetchDatasetImgs(item.id, 1, 4).then(res => {
                item.imgs = res.imgs;
                item.totalImgCount = res.total;
            })));
        }).then(() => {
            runInAction(() => this._datasets = datasets);
            return Promise.resolve();
        });
    }

    @action
    setImgText(index: number, prompt: string){
        if(!this._curDataset){
            return;
        }
        this._curDataset.imgs[index].changeText(prompt);
    }

    @action
    createDataset(title: string, description: string): Promise<DatasetInfo>{
        if(this.datasets.some(item => item.title === title)){
            return Promise.reject(new Error("不能和现有数据集重名"));
        }

        return newDataset(title, description).then(res => {
            runInAction(() => this._datasets = [res].concat(this._datasets));
            return Promise.resolve(res);
        });
    }

    @action
    setCurDataset(a: DatasetInfo | null){
        this._curDataset = a;
    }

    private uploadImgsInner(datasetId: string, files: File[], onProgress:(a: number) => void, index: number = 0, newImgs: DatasetImgInfo[] = []): Promise<DatasetImgInfo[]>{
        if(index >= files.length){
            return Promise.resolve(newImgs);
        }

        return getImgInfo(files[index]).then(res => {
            return uploadImgToServer(base64ToFile(res.data));
        }).then(url => {
            return addDatasetImg(datasetId, url);
        }).then(newImgInfo => {
            newImgs.push(newImgInfo);
            onProgress((index + 1) / files.length);
            return this.uploadImgsInner(datasetId, files, onProgress, index + 1, newImgs);
        }).catch(reason => {
            msgbox(reason.message, MSG_TYPE.ERROR)
            return Promise.reject(reason);
        });
    }

    @action
    uploadImgs(files: File[], onProgress:(a: number) => void, index: number = 0, newImgs: DatasetImgInfo[] = []): Promise<void>{
        if(files.length <= 0){
            return Promise.resolve();
        }

        return this.deleteImgs(this._curDataset!.imgs.map(item => item.id))
            .then(() => this.uploadImgsInner(this._curDataset!.id, files.slice(0, MAX_DATASET_IMT_COUNT), onProgress, index, newImgs))
            .then(res => {
                runInAction(() => {
                    this._curDataset!.imgs = res;
                    this._curDataset!.totalImgCount = res.length;
                })
                return Promise.resolve();
            });
    }

    @action
    getDatasetImgs(datasetId: string): Promise<void>{
        return fetchDatasetImgs(datasetId).then(res => {
            runInAction(() => {
                const index = this._datasets.findIndex(item => item.id === datasetId);
                if(index === -1){
                    return;
                }
                this._datasets[index].imgs = res.imgs;
            })
            return Promise.resolve();
        });
    }

    @action
    deleteImgs(ids: string[]): Promise<void>{
        if(ids.length <= 0){
            return Promise.resolve();
        }
        return delDatasetImgs(this._curDataset!.id, ids)
            .then(() => {
                runInAction(() => {
                    const _ids = new Set<string>(ids);
                    this._curDataset!.imgs = this._curDataset!.imgs.filter(item => !_ids.has(item.id));
                    this._curDataset!.totalImgCount = this._curDataset!.imgs.length;
                })
                return Promise.resolve();
            });
    }

    @action
    addImgs(files: File[], onProgress:(a: number) => void, index: number = 0, newImgs: DatasetImgInfo[] = []): Promise<void>{
        if(files.length <= 0){
            return Promise.resolve();
        }

        const len = this._curDataset!.imgs.length;
        if(len >= MAX_DATASET_IMT_COUNT){
            return Promise.resolve();
        }

        return this.uploadImgsInner(this._curDataset!.id, files.slice(0, MAX_DATASET_IMT_COUNT - len), onProgress, index, newImgs).then(res => {
            runInAction(() => {
                this._curDataset!.imgs = this._curDataset!.imgs.concat(res);
                this._curDataset!.totalImgCount += res.length;
            });
        });
    }

    private clipImgsInner = (promptPrefix: string, clipMethod: number, preferWidth: number, preferHeight: number, imgElement: HTMLImageElement, canvas: HTMLCanvasElement, onProgress:(a: number) => void, index: number = 0, res: DatasetImgInfo[] = []): Promise<DatasetImgInfo[]> => {
        if(index >= this._curDataset!.imgs.length){
            return Promise.resolve(res);
        }

        const imgInfo = this._curDataset!.imgs[index];
        if(imgInfo.text.length > 0){
            res.push(imgInfo);
            onProgress((index + 1) / this._curDataset!.imgs.length);
            return this.clipImgsInner(promptPrefix, clipMethod, preferWidth, preferHeight, imgElement, canvas, onProgress, index + 1, res);
        }

        let clipedImg = "";
        let url: string = "";
        let prompt: string = "";
        return [clipImgFocus, clipImgDummy][clipMethod](imgInfo.url, preferWidth, preferHeight, imgElement, canvas)
            .then(clipImg => {
                clipedImg = clipImg;
                return fetchPrompt(clipedImg);
            }).then(res => {
                prompt = res;
                return uploadImgToServer(base64ToFile(clipedImg));
            }).then(imgUrl => {
                url = imgUrl;
                return updateDatasetImg(this._curDataset!.id, imgInfo.id, imgUrl, prompt);
            }).then(() => {
                this._isCliping = false;
                res.push(new DatasetImgInfo(url, preferWidth, preferHeight, `${promptPrefix},${prompt}`, imgInfo.id));
                onProgress((index + 1) / this._curDataset!.imgs.length);
                return this.clipImgsInner(promptPrefix, clipMethod, preferWidth, preferHeight, imgElement, canvas, onProgress, index + 1, res);
            });
    }

    @action
    //clipMethod 0 = 中央 1 = 无
    clipImgs(promptPrefix: string, clipMethod: number, preferWidth: number, preferHeight: number, onProgress:(a: number) => void): Promise<void>{
        const img = new Image();
        img.crossOrigin = "anonymous";
        this._isCliping = true;
        return this.clipImgsInner(promptPrefix, clipMethod, preferWidth, preferHeight, img, document.createElement("canvas"), onProgress).then(res => {
            runInAction(() => {
                this._curDataset!.imgs = res;
            });
            return Promise.resolve();
        });
    }

    @action
    addToHead(prompt: string){
        if(!this._curDataset){
            return;
        }
        this._curDataset.imgs.forEach(item => item.changeText(`${prompt},${item.text}`));
    }

    @action
    addToTail(prompt: string){
        if(!this._curDataset){
            return;
        }
        this._curDataset.imgs.forEach(item => item.changeText(`${item.text},${prompt}`));
    }

    @action
    startTrain(onProgress:(a: number) => void, progress: number = 0): Promise<void>{
        if(this._stopTrain || progress >= 100){
            this._stopTrain = false;
            return Promise.resolve();
        }
        onProgress(progress / 100);
        return delay(100).then(() => this.startTrain(onProgress, progress + 1));
    }

    @action
    stopTrain(): void{
        this._stopTrain = true;
    }

    @action
    updateDataset(index: number, title: string, description: string){
        const dataset = this._datasets[index];
        dataset.title = title;
        dataset.description = description;
    }

    @action
    updateDatasetImgTexts(): Promise<void>{
        return Promise.all(
            this._curDataset!.imgs
                .filter(item => item.isTextChange)
                .map(item => {
                    return updateDatasetImg(this._curDataset!.id, item.id, item.url, item.text)
                        .then(item => item.isTextChange = false);
        })).then(() => Promise.resolve());
    }

    @action
    deleteDataset(id: string): Promise<void>{
        return delDataset(id).then(() => {
            runInAction(() => this._datasets = this._datasets.filter(item => item.id !== id));
            return Promise.resolve();
        });
    }

    @action
    copyDataset(index: number): Promise<void>{
        const target = this._datasets[index];
        return newDataset(`${target.title}-1`, target.description).then(newDataset => {
            return Promise.all(target.imgs.map(item => {
                    const ext = item.url.substring(item.url.lastIndexOf(".") + 1);
                    return fetch(item.url).then(res => res.blob()).then(res => new File([res], `${Date.now()}.${ext}`, {type: `image/${ext}`}))
                })).then(files => {
                    return this.uploadImgsInner(newDataset.id, files, a => a)
                }).then(imgInfos => {
                    newDataset.imgs = imgInfos;
                    newDataset.totalImgCount = imgInfos.length;
                    this._datasets.push(newDataset);
                    return Promise.resolve();
                });
        });
    }
}